import { useEffect, useState } from 'react';

// Kept apart from `useIdlePreloader`: the root route, `scheduleOnIdle` and the deferred-section
// gates read these on every page, and must not pull in the preloader's access rules, stores and
// navigation (an import cycle through `scheduleOnIdle` and the logger, and boot weight on public
// pages).

/**
 * Grace period after `window.load` (or hook mount, whichever comes later)
 * before background preloading is allowed. Gives the browser time to finish
 * post-load work (LCP candidate updates, deferred image decoding, late-arriving
 * fonts) without competing for bandwidth from the idle preloader.
 */
export const POST_LOAD_GRACE_MS = 1500;

/**
 * Returns `true` once two conditions are both satisfied:
 *   1. The page has fully loaded (`window.load` has fired, equivalent to
 *      `document.readyState === 'complete'`).
 *   2. A {@link POST_LOAD_GRACE_MS} grace period has elapsed.
 *
 * The reference point for the grace period is whichever of `load` or
 * hook-mount happens **later**:
 *   - If the page is still loading at mount → grace starts at `load` event.
 *   - If the page is already loaded at mount (typical when this hook is
 *     mounted by `AuthenticatedLayout` after auth, well after `load`) →
 *     grace starts at mount. Using mount-time as the reference is intentional:
 *     a fixed window from mount is sufficient breathing room and avoids
 *     tracking historical `loadEventEnd` timings.
 *
 * Used to gate background preloading so it cannot fire mid-pageload for slow
 * p75 users (where the `react-idle-timer` "idle" state can trigger while the
 * user is still waiting for the page to render).
 *
 * Exported for unit testing — not intended as a general-purpose API.
 */
export function usePageSettled(): boolean {
    const [settled, setSettled] = useState(false);

    useEffect(() => {
        let timer: number | undefined;
        const scheduleSettled = () => {
            timer = window.setTimeout(() => setSettled(true), POST_LOAD_GRACE_MS);
        };

        if (document.readyState === 'complete') {
            scheduleSettled();
            return () => {
                if (timer !== undefined) window.clearTimeout(timer);
            };
        }

        const onLoad = () => scheduleSettled();
        window.addEventListener('load', onLoad, { once: true });
        return () => {
            window.removeEventListener('load', onLoad);
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, []);

    return settled;
}
